import {
  Button,
  type ButtonProps,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiXCircle } from "react-icons/fi";
import {
  BidStatus,
  type BuyerBidDetailDto,
  useGetBidRejectionDocumentsQuery,
  useGetSourcingEventQuery,
  useRejectBidMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";
import {
  type RejectionSubmission,
  RejectSupplierModal,
} from "../../../common/rejection/RejectSupplierModal";
import { rejectionRegimeOf } from "../../../common/rejection/rejectionGrounds";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { RejectedSummary } from "./BidRejectionSummary";

interface Props {
  detail: BuyerBidDetailDto;
  onViewRejectionDetails?: () => void;
}

export const BidRejectionActionCard = ({
  detail,
  onViewRejectionDetails,
}: Props) => {
  if (detail.bid.status === BidStatus.Rejected) {
    return (
      <RejectedSummary detail={detail} onViewDetails={onViewRejectionDetails} />
    );
  }
  if (detail.bid.status !== BidStatus.Submitted) {
    return null;
  }
  return <RejectAction detail={detail} />;
};

const RejectAction = ({ detail }: Props) => {
  const { t } = useTranslation();

  return (
    <SectionCard icon={FiXCircle} title={t("Actions")}>
      <RejectBidButton detail={detail} />
    </SectionCard>
  );
};

export const RejectBidButton = ({
  detail,
  size = "md",
  width = "full",
  label,
  onRejected,
}: {
  detail: BuyerBidDetailDto;
  size?: ButtonProps["size"];
  width?: ButtonProps["width"];
  label?: string;
  onRejected?: () => void;
}) => {
  const { t } = useTranslation();
  const disclosure = useDisclosure();

  if (detail.replacedByLaterBid) {
    return (
      <Text fontSize="sm" color="smMuted">
        {detail.replacedByRoundNumber != null
          ? t("This bid has been replaced by the supplier's bid in round {{round}}. Reject that bid instead.", {
              round: detail.replacedByRoundNumber,
            })
          : t("This bid has been replaced by a later bid from the supplier. Reject that bid instead.")}
      </Text>
    );
  }

  return (
    <>
      <Button
        size={size}
        colorScheme="red"
        variant="outline"
        width={width}
        onClick={disclosure.onOpen}
      >
        {label ?? t("Avvis leverandør")}
      </Button>
      {disclosure.isOpen ? (
        <RejectBidModal
          detail={detail}
          isOpen={disclosure.isOpen}
          onClose={disclosure.onClose}
          onRejected={onRejected}
        />
      ) : null}
    </>
  );
};

const personName = (person: { firstName?: string | null; lastName?: string | null } | null | undefined) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || null;

const RejectBidModal = ({
  detail,
  isOpen,
  onClose,
  onRejected,
}: {
  detail: BuyerBidDetailDto;
  isOpen: boolean;
  onClose: () => void;
  onRejected?: () => void;
}) => {
  const [rejectBid, rejectState] = useRejectBidMutation();
  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;
  const { data, refetch } = useGetBidRejectionDocumentsQuery({
    eventId,
    bidId,
  });
  const runsPublicProcurements = useRunsPublicProcurements();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const regime = useMemo(() => rejectionRegimeOf(event), [event]);
  const privateContext = runsPublicProcurements
    ? undefined
    : {
        eventTitle: detail.eventTitle ?? "",
        supplierContactName: detail.ownerName,
        responsibleBuyerName: personName(event?.responsiblePerson),
        buyerOrganizationName: selectedOrg.name,
      };

  const submit = async (submission: RejectionSubmission) => {
    await rejectBid({
      eventId,
      bidId,
      rejectBidRequestDto: submission,
    }).unwrap();
    onClose();
    onRejected?.();
  };

  return (
    <RejectSupplierModal
      isOpen={isOpen}
      onClose={onClose}
      supplierName={detail.ownerName}
      organizationNumber={detail.ownerOrganizationNumber}
      foaPart={detail.foaPart}
      regime={regime}
      includeTenderGrounds
      privateContext={privateContext}
      outsideProcurementAct={event?.outsideProcurementAct ?? false}
      attachments={{
        entityType: "BidV2Rejection",
        entityId: bidId,
        eventId,
        documents: data?.documents ?? [],
        refetch,
      }}
      onSubmit={submit}
      isSubmitting={rejectState.isLoading}
    />
  );
};
